import { useState } from "react";
import { Card } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import type * as API from "@shared-core/data/api-types";
import { FileText, Building2, Truck } from "lucide-react";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { OrderTrackingModal } from "../delivery/OrderTrackingModal";

interface JobCardProps {
  job: API.JobViewItem;
  onClick?: () => void;
  onEstimateClick?: (estimateId: string) => void;
}

// Human-readable labels for the job's stock model (lifecycle). Unknown/absent
// values render nothing rather than a raw code.
const STOCK_MODEL_LABEL: Record<string, string> = {
  adhoc: "Ad-hoc",
  pod: "POD",
  stock_customer: "Customer stock",
  stock_ours: "Our stock",
};

function StockModelBadge({ stockModel }: { stockModel?: string | null }) {
  if (!stockModel) return null;
  const label = STOCK_MODEL_LABEL[stockModel] ?? stockModel;
  return <Badge variant="neutral">{label}</Badge>;
}

export function JobCard({ job, onClick, onEstimateClick }: JobCardProps) {
  const [trackingOrderNo, setTrackingOrderNo] = useState<string | null>(null);

  const handleTrackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTrackingOrderNo(job.job_no ?? null);
  };

  const handleEstimateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEstimateClick && job.estimate_id) {
      onEstimateClick(job.estimate_id);
    }
  };

  const isInvoiced = !!job.invoiced_at;

  return (
    <>
      <Card
        className="p-4 hover:shadow-md transition-shadow cursor-pointer"
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {job.job_no ? (
                <Link
                  to={paths.jobWorkspace({ jobNo: job.job_no })}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-[length:var(--text-base)] hover:underline text-[color:var(--color-primary)]"
                >
                  {job.job_no}
                </Link>
              ) : (
                <span className="font-medium text-[length:var(--text-base)]">
                  {job.job_no}
                </span>
              )}
              {isInvoiced && <Badge variant="success">Invoiced</Badge>}
              {!isInvoiced && <Badge variant="warning">Work in progress</Badge>}
              <StockModelBadge stockModel={job.stock_model} />
            </div>

            {job.customer && (
              <div className="mb-2 flex items-center gap-1.5 text-sm">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <Link
                  to={paths.customerDetail({ customerId: job.customer.id })}
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-[color:var(--color-primary)] hover:underline truncate"
                >
                  {job.customer.name}
                </Link>
              </div>
            )}

            <p className="text-sm text-muted-foreground mb-3 break-words">
              {job.description}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm text-muted-foreground [&>div]:min-w-0">
              <div>
                <span className="block text-xs">Booked Value</span>
                <span className="block font-medium text-foreground truncate">
                  £
                  {job.booked_value.toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div>
                <span className="block text-xs">Contribution</span>
                <span className="block font-medium text-foreground truncate">
                  £
                  {job.contribution_amt.toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  <span className="text-xs text-muted-foreground">
                    (
                    {job.booked_value
                      ? (
                          (job.contribution_amt / job.booked_value) *
                          100
                        ).toFixed(1)
                      : "0.0"}
                    %)
                  </span>
                </span>
              </div>
              <div>
                <span className="block text-xs">Booking Date</span>
                <span className="block font-medium text-foreground truncate">
                  {job.ordered_at ? formatDateOrDash(job.ordered_at) : "—"}
                </span>
              </div>
              <div>
                <span className="block text-xs">Due Date</span>
                <span className="block font-medium text-foreground truncate">
                  {job.due_at ? formatDateOrDash(job.due_at) : "—"}
                </span>
              </div>
              <div>
                <span className="block text-xs">Invoice Date</span>
                <span className="block font-medium text-foreground truncate">
                  {isInvoiced ? formatDateOrDash(job.invoiced_at) : "—"}
                </span>
              </div>
            </div>

            {job.estimate_id && (
              <button
                onClick={handleEstimateClick}
                className="mt-2 flex items-center gap-1 text-xs text-[color:var(--color-primary)] hover:underline"
              >
                <FileText className="w-3 h-3" />
                View Related Estimate
              </button>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleTrackClick}
              className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Track delivery"
            >
              <Truck className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {trackingOrderNo && (
        <OrderTrackingModal
          orderNo={trackingOrderNo}
          onClose={() => setTrackingOrderNo(null)}
        />
      )}
    </>
  );
}
